import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";
import api from "@/lib/api";

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get("/analytics/global").then((r) => setStats(r.data));
    api.get("/campaigns").then((r) => setCampaigns(r.data));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono">
          Performance
        </div>
        <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">
          Analytics
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          ["Sent", stats?.sent ?? 0],
          ["Open", `${stats?.open_rate ?? 0}%`],
          ["Click", `${stats?.click_rate ?? 0}%`],
          ["Reply", `${stats?.reply_rate ?? 0}%`],
          ["Bounce", `${stats?.bounce_rate ?? 0}%`],
          ["Unsub", stats?.unsubscribed ?? 0],
        ].map(([label, val]) => (
          <div
            key={label}
            className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-4"
            data-testid={`analytics-kpi-${label.toLowerCase()}`}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">
              {label}
            </div>
            <div className="font-mono text-2xl mt-2">{val}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
            Sending volume - 14d
          </div>
          <div className="h-64 mt-4 min-h-[256px]">
            <ResponsiveContainer>
              <AreaChart data={stats?.timeseries || []}>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => v.slice(5)}
                  stroke="#525252"
                  fontSize={11}
                />
                <YAxis stroke="#525252" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: "#0a0a0a",
                    border: "1px solid #1f1f1f",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="sent"
                  stroke="#fafafa"
                  fill="rgba(250,250,250,0.15)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
            Engagement - 14d
          </div>
          <div className="h-64 mt-4 min-h-[256px]">
            <ResponsiveContainer>
              <BarChart data={stats?.timeseries || []}>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => v.slice(5)}
                  stroke="#525252"
                  fontSize={11}
                />
                <YAxis stroke="#525252" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: "#0a0a0a",
                    border: "1px solid #1f1f1f",
                  }}
                />
                <Bar dataKey="opens" fill="#10b981" />
                <Bar dataKey="clicks" fill="#0ea5e9" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-[#1a1a1a] text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">
          Per-campaign
        </div>
        {campaigns.length === 0 ? (
          <div className="text-neutral-500 text-sm p-10 text-center">
            No campaigns yet.
          </div>
        ) : (
          <table className="w-full">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">
              <tr className="border-b border-[#1a1a1a]">
                <th className="text-left px-6 py-3">Campaign</th>
                <th className="text-right px-6 py-3">Sent</th>
                <th className="text-right px-6 py-3">Open %</th>
                <th className="text-right px-6 py-3">Reply %</th>
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {campaigns.map((c) => (
                <tr key={c.id} className="border-b border-[#1a1a1a] hover:bg-[#111]">
                  <td className="px-6 py-3 text-white">{c.name}</td>
                  <td className="px-6 py-3 text-right">{c.sent_count}</td>
                  <td className="px-6 py-3 text-right">{c.open_rate}%</td>
                  <td className="px-6 py-3 text-right">{c.reply_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
